import { DeleteOutlined, EditOutlined, TeamOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  App,
  Button,
  Card,
  Checkbox,
  Drawer,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiError, apiFetch } from "../api/client";
import { toBoolean } from "../api/format";
import { useServerNotifications } from "../api/notifications";
import type {
  ListResponse,
  NotificationsResponse,
  ZoneArchiveRow,
  ZoneEventCongregationRow,
  ZoneEventListResponse,
} from "../api/types";
import { DataTable } from "../components/DataTable";
import type { ExportableColumn } from "../components/DataTable";

interface EventFormValues {
  event_type: number;
  additional_notes: string;
}

/**
 * Sidebar entry "zone_assignations" → /zone_assignation/assignations.
 *
 * The circumscription's own calendar: the weeks the zone plans something of its
 * own — a circuit overseer's visit, an assembly, the Memorial — and which of
 * its congregations take part.
 *
 * A zone event is what makes a week unavailable for the congregations' normal
 * planning, so the drill-down is the point of the screen: assigning a
 * congregation writes a local assignation for it, and asking for its speakers
 * pulls them along.
 */
export function ZoneAssignationPage() {
  const { t } = useTranslation();
  const notify = useServerNotifications();
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<EventFormValues>();

  const [editing, setEditing] = useState<ZoneArchiveRow | null>(null);
  const [congregationsOf, setCongregationsOf] = useState<ZoneArchiveRow | null>(null);

  const events = useQuery({
    queryKey: ["zone_assignation", "events"],
    queryFn: () => apiFetch<ZoneEventListResponse>("/zone_assignation/events"),
  });

  const showError = useCallback(
    (error: unknown) => {
      if (error instanceof ApiError && error.notifications) {
        notify(error.notifications);
        return;
      }
      void message.error(error instanceof Error ? error.message : String(error));
    },
    [notify, message],
  );

  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["zone_assignation"] });
    // The congregations' own calendars are written by the same action.
    void queryClient.invalidateQueries({ queryKey: ["archive"] });
  }, [queryClient]);

  const save = useMutation({
    mutationFn: ({ week, values }: { week: string; values: EventFormValues }) =>
      apiFetch<NotificationsResponse>(`/zone_assignation/events/${week}`, {
        method: "PUT",
        body: values,
      }),
    onSuccess: (data) => {
      notify(data.notifications);
      setEditing(null);
      refresh();
    },
    onError: showError,
  });

  const cancelEvent = useMutation({
    mutationFn: (week: string) =>
      apiFetch<NotificationsResponse>(`/zone_assignation/events/${week}`, { method: "DELETE" }),
    onSuccess: (data) => {
      notify(data.notifications);
      refresh();
    },
    onError: showError,
  });

  const openEditor = useCallback(
    (row: ZoneArchiveRow) => {
      setEditing(row);
      form.resetFields();
      form.setFieldsValue({
        event_type: row.event_type ?? undefined,
        additional_notes: row.additional_notes ?? "",
      } as EventFormValues);
    },
    [form],
  );

  const busy = save.isPending || cancelEvent.isPending;
  const eventTypes = events.data?.event_types ?? [];

  const columns = useMemo<ExportableColumn<ZoneArchiveRow>[]>(
    () => [
      {
        title: t("activerecord.attributes.local_assignation.week_begin"),
        dataIndex: "week_label",
        width: 190,
        fixed: "left",
        // The tag goes on its own line rather than next to the date: side by
        // side the two share the column's width and the date is what breaks.
        render: (value: string, row) =>
          row.modified ? (
            <Space orientation="vertical" size={0} align="start">
              <span style={{ whiteSpace: "nowrap" }}>{value}</span>
              <Tag color="green" style={{ marginInlineEnd: 0 }}>
                {t("icon.modified")}
              </Tag>
            </Space>
          ) : (
            value
          ),
        // The tag is part of what the week says, so it has to survive the trip
        // into a file — there is no second column carrying it.
        exportValue: (row) =>
          row.modified ? `${row.week_label} (${t("icon.modified")})` : row.week_label,
      },
      {
        title: t("activerecord.attributes.local_assignation.event_type"),
        dataIndex: "event_type_label",
        width: 200,
        ellipsis: true,
      },
      {
        title: t("activerecord.attributes.local_assignation.additional_notes"),
        dataIndex: "additional_notes",
        ellipsis: true,
      },
      {
        key: "actions",
        fixed: "right",
        width: 120,
        render: (_value, row) => (
          <Space size={4}>
            <Tooltip title={row.has_event ? t("form.update") : t("events.event")}>
              <Button
                size="small"
                icon={<EditOutlined />}
                disabled={busy}
                onClick={(event) => {
                  event.stopPropagation();
                  openEditor(row);
                }}
              />
            </Tooltip>
            {row.has_event && (
              <>
                <Tooltip title={t("zone_assignation.congregation_details")}>
                  <Button
                    size="small"
                    icon={<TeamOutlined />}
                    disabled={busy}
                    onClick={(event) => {
                      event.stopPropagation();
                      setCongregationsOf(row);
                    }}
                  />
                </Tooltip>
                <Tooltip title={t("zone_assignation.cancel")}>
                  <Button
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    disabled={busy}
                    onClick={(event) => {
                      event.stopPropagation();
                      modal.confirm({
                        title: t("dialog.confirmation_title"),
                        content: (
                          <>
                            <Typography.Paragraph>{row.week_label}</Typography.Paragraph>
                            {t("zone_assignation.confirm_cancel_zone_event")}
                          </>
                        ),
                        okText: t("dialog.answer_yes"),
                        okButtonProps: { danger: true },
                        cancelText: t("dialog.answer_no"),
                        onOk: () => cancelEvent.mutate(row.week_begin),
                      });
                    }}
                  />
                </Tooltip>
              </>
            )}
          </Space>
        ),
      },
    ],
    [t, busy, openEditor, cancelEvent, modal],
  );

  return (
    <Card title={t("navigation.circumscription_events")}>
      <DataTable<ZoneArchiveRow>
        columns={columns}
        rows={events.data?.data}
        loading={events.isPending || busy}
        error={events.error}
        rowKey="week_begin"
        rowClassName={(row) => (row.has_event ? "" : "zone-empty-week")}
        exportAs={t("navigation.circumscription_events")}
      />

      <style>{`.zone-empty-week { opacity: 0.55; }`}</style>

      <Modal
        open={editing !== null}
        title={editing?.week_label}
        onCancel={() => setEditing(null)}
        okText={t("form.update")}
        okButtonProps={{ loading: save.isPending }}
        onOk={() => form.submit()}
        destroyOnHidden
      >
        <Form<EventFormValues>
          form={form}
          layout="vertical"
          onFinish={(values) => save.mutate({ week: editing!.week_begin, values })}
        >
          <Form.Item
            name="event_type"
            label={t("activerecord.attributes.local_assignation.event_type")}
          >
            {/* Locked once the week exists: the congregations' own assignations
                were written from this type. */}
            <Select options={eventTypes} disabled={editing?.has_event} style={{ width: 280 }} />
          </Form.Item>

          <Form.Item
            name="additional_notes"
            label={t("activerecord.attributes.local_assignation.additional_notes")}
          >
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      {congregationsOf && (
        <CongregationsDrawer
          event={congregationsOf}
          onClose={() => setCongregationsOf(null)}
          onChanged={refresh}
        />
      )}
    </Card>
  );
}

/**
 * Which congregations take part, and whether their speakers are asked along.
 * The two flags are not independent: the server will not keep "speakers
 * requested" for a congregation that is not taking part, and corrects one of
 * the two rather than refusing.
 */
function CongregationsDrawer({
  event,
  onClose,
  onChanged,
}: {
  event: ZoneArchiveRow;
  onClose: () => void;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const notify = useServerNotifications();
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();

  const queryKey = useMemo(
    () => ["zone_assignation", "congregations", event.week_begin],
    [event.week_begin],
  );

  const congregations = useQuery({
    queryKey,
    queryFn: () =>
      apiFetch<ListResponse<ZoneEventCongregationRow>>(
        `/zone_assignation/events/${event.week_begin}/congregations`,
      ),
  });

  const showError = useCallback(
    (error: unknown) => {
      if (error instanceof ApiError && error.notifications) {
        notify(error.notifications);
        return;
      }
      void message.error(error instanceof Error ? error.message : String(error));
    },
    [notify, message],
  );

  const afterChange = useCallback(
    (data: NotificationsResponse) => {
      notify(data.notifications);
      void queryClient.invalidateQueries({ queryKey });
      onChanged();
    },
    [notify, queryClient, queryKey, onChanged],
  );

  const update = useMutation({
    mutationFn: ({
      row,
      assigned,
      speakerRequested,
    }: {
      row: ZoneEventCongregationRow;
      assigned: boolean;
      speakerRequested: boolean;
    }) =>
      apiFetch<NotificationsResponse>(
        `/zone_assignation/events/${event.week_begin}/congregations/${row.id}`,
        { method: "PUT", body: { assigned, speaker_requested: speakerRequested } },
      ),
    onSuccess: afterChange,
    onError: showError,
  });

  const requestAll = useMutation({
    mutationFn: (speakerRequested: boolean) =>
      apiFetch<NotificationsResponse>(
        `/zone_assignation/events/${event.week_begin}/congregations`,
        { method: "PUT", body: { speaker_requested: speakerRequested } },
      ),
    onSuccess: afterChange,
    onError: showError,
  });

  const busy = update.isPending || requestAll.isPending;

  const askAndRequestAll = useCallback(
    (withSpeakers: boolean) => {
      // Writes an assignation for every congregation of the zone at once.
      modal.confirm({
        title: t("dialog.confirmation_title"),
        content: withSpeakers
          ? t("zone_assignation.request_congregations_and_speakers")
          : t("zone_assignation.request_congregations"),
        okText: t("dialog.answer_yes"),
        cancelText: t("dialog.answer_no"),
        onOk: () => requestAll.mutate(withSpeakers),
      });
    },
    [modal, t, requestAll],
  );

  const columns = useMemo<ExportableColumn<ZoneEventCongregationRow>[]>(
    () => [
      {
        title: t("activerecord.attributes.congregation.congregation_code"),
        dataIndex: "congregation_code",
        width: 90,
      },
      {
        title: t("activerecord.attributes.congregation.denomination"),
        dataIndex: "denomination",
        ellipsis: true,
      },
      {
        title: t("activerecord.attributes.local_assignation.requested"),
        dataIndex: "assigned",
        width: 140,
        render: (value: boolean, row) => (
          <Checkbox
            checked={value}
            disabled={busy}
            onChange={(change) =>
              update.mutate({
                row,
                assigned: change.target.checked,
                // Dropping a congregation drops its speakers with it.
                speakerRequested: change.target.checked && row.speaker_requested,
              })
            }
          />
        ),
      },
      {
        title: t("zone_assignation.speaker_requested"),
        dataIndex: "speaker_requested",
        width: 160,
        render: (value: boolean, row) => (
          <Checkbox
            checked={value}
            disabled={busy}
            onChange={(change) =>
              update.mutate({
                row,
                // Asking for the speakers implies the congregation takes part.
                assigned: change.target.checked || row.assigned,
                speakerRequested: change.target.checked,
              })
            }
          />
        ),
      },
      {
        title: t("congregation.headers.contact_person"),
        key: "contact",
        width: 200,
        ellipsis: true,
        render: (_value, row) =>
          [row.contact_first_name, row.contact_last_name].filter(Boolean).join(" "),
        // No dataIndex to read from: the name is put together here.
        exportValue: (row) =>
          [row.contact_first_name, row.contact_last_name].filter(Boolean).join(" "),
      },
      {
        title: t("activerecord.attributes.congregation.phone"),
        dataIndex: "phone",
        width: 150,
      },
      {
        title: t("activerecord.attributes.congregation.email"),
        dataIndex: "email",
        width: 200,
        ellipsis: true,
      },
      {
        title: t("activerecord.attributes.congregation.active"),
        dataIndex: "active",
        width: 90,
        render: (value) => (toBoolean(value) ? null : <Tag>{t("speaker.disabled")}</Tag>),
        // The API answers 0/1 here, and the column only marks the exception.
        exportValue: (row) => (toBoolean(row.active) ? "" : t("speaker.disabled")),
      },
    ],
    [t, busy, update],
  );

  return (
    <Drawer
      open
      onClose={onClose}
      size={1100}
      destroyOnHidden
      title={[event.week_label, event.event_type_label].filter(Boolean).join(" · ")}
      extra={
        <Space>
          <Button disabled={busy} onClick={() => askAndRequestAll(false)}>
            {t("zone_assignation.request_congregations")}
          </Button>
          <Button type="primary" disabled={busy} onClick={() => askAndRequestAll(true)}>
            {t("zone_assignation.request_congregations_and_speakers")}
          </Button>
        </Space>
      }
    >
      <DataTable<ZoneEventCongregationRow>
        columns={columns}
        rows={congregations.data?.data}
        loading={congregations.isPending || busy}
        error={congregations.error}
        exportAs={[event.week_label, event.event_type_label].filter(Boolean).join(" · ")}
      />
    </Drawer>
  );
}
